/**
 * PackageFormDialog Component
 * Modal dialog for creating or editing a package
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Plus, X, Edit } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import { FormattedNumberTextField } from '../common/FormattedNumberTextField';
import { PackageFeatureToggles } from './PackageFeatureToggles';
import { PackageIntegrationToggles } from './PackageIntegrationToggles';
import { PackageIntroFields } from './PackageIntroFields';
import { validateFeatureLimits } from './packageFeatureLimitValidation';
import {
  formatRateHint,
  usdToVnd,
  useUsdVndRate,
  vndToUsd,
} from '../../services/exchangeRate.service';
import { PricingCalculatorPanel } from './pricing/PricingCalculatorPanel';
import { usePackageFeatureCatalog } from '../../services/package.service';
import { useBreakEvenGuard } from './pricing/useBreakEvenGuard';

export interface PackageFormData {
  publicId?: string; // Only for edit mode
  code: string;
  name: string;
  descriptionEn: string;
  descriptionVi: string;
  highlightsEn: string;
  highlightsVi: string;
  price: number;
  priceVnd?: number;
  maxAgents: number;
  tokensPerMonth: number;
  advanceTokensPerMonth: number;
  duration: number;
  isActive: boolean;
  allowDynamicTheme: boolean;
  features: string[];
  featureLimits: Record<string, number | null>;
  featureDailyLimits: Record<string, number | null>;
  integrationEntitlements: string[];
  integrationLimits: Record<string, number>;
}

interface PackageFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PackageFormData) => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  initialData?: PackageFormData;
}

const PACKAGE_CODE_PATTERN = /^[A-Z0-9_-]+$/;

export const PackageFormDialog: React.FC<PackageFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  mode,
  initialData,
}) => {
  const notification = useNotification();

  const getInitialFormData = (): PackageFormData => {
    if (mode === 'edit' && initialData) {
      return {
        ...initialData,
        priceVnd: initialData.priceVnd ?? 0,
        features: initialData.features ?? [],
        featureLimits: initialData.featureLimits ?? {},
        featureDailyLimits: initialData.featureDailyLimits ?? {},
        descriptionEn: initialData.descriptionEn ?? '',
        descriptionVi: initialData.descriptionVi ?? '',
        highlightsEn: initialData.highlightsEn ?? '',
        highlightsVi: initialData.highlightsVi ?? '',
      };
    }
    return {
      code: '',
      name: '',
      descriptionEn: '',
      descriptionVi: '',
      highlightsEn: '',
      highlightsVi: '',
      price: 0,
      priceVnd: 0,
      maxAgents: 1,
      tokensPerMonth: 100000,
      advanceTokensPerMonth: 0,
      duration: 30,
      isActive: true,
      allowDynamicTheme: false,
      features: [],
      featureLimits: {},
      featureDailyLimits: {},
      integrationEntitlements: [],
      integrationLimits: {},
    };
  };

  const [formData, setFormData] = useState<PackageFormData>(getInitialFormData());
  const [breakEvenUsd, setBreakEvenUsd] = useState<number | null>(null);
  const breakEvenGuard = useBreakEvenGuard();
  const { data: featureCatalog = [] } = usePackageFeatureCatalog();
  const featureLimitErrors = useMemo(
    () =>
      validateFeatureLimits(featureCatalog, formData.featureLimits, formData.featureDailyLimits),
    [featureCatalog, formData.featureDailyLimits, formData.featureLimits]
  );

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialData]);

  const handleClose = () => {
    onClose();
  };

  const { data: rate } = useUsdVndRate();

  // Editing one price fills the other from the daily exchange rate; both stay editable.
  const handleUsdChange = (usd: number) => {
    setFormData((prev) => ({
      ...prev,
      price: usd,
      ...(rate ? { priceVnd: usdToVnd(usd, rate.vndPerUsd) } : {}),
    }));
  };

  const handleVndChange = (vnd: number) => {
    setFormData((prev) => ({
      ...prev,
      priceVnd: vnd,
      ...(rate ? { price: vndToUsd(vnd, rate.vndPerUsd) } : {}),
    }));
  };

  const handleFieldChange = <K extends keyof PackageFormData>(
    field: K,
    value: PackageFormData[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    // Validation
    const normalizedCode = formData.code.trim().toUpperCase();
    if (!PACKAGE_CODE_PATTERN.test(normalizedCode)) {
      notification.error(
        'Package code may contain only uppercase letters, numbers, underscores, and hyphens'
      );
      return;
    }

    if (!formData.name.trim()) {
      notification.error('Package name is required');
      return;
    }

    if (formData.price < 0) {
      notification.error('Price (USD) must be non-negative');
      return;
    }

    if (formData.priceVnd !== undefined && formData.priceVnd < 0) {
      notification.error('Price (VND) must be non-negative');
      return;
    }

    if (formData.maxAgents < 0) {
      notification.error('Max agents must be non-negative');
      return;
    }

    if (formData.tokensPerMonth <= 0) {
      notification.error('Credits per cycle must be positive');
      return;
    }

    if (formData.advanceTokensPerMonth < 0) {
      notification.error('Advance credits per cycle must be non-negative');
      return;
    }

    if (formData.duration <= 0) {
      notification.error('Duration must be positive');
      return;
    }

    const limitErrors = Object.values(featureLimitErrors);
    if (limitErrors.length > 0) {
      notification.error(limitErrors[0]);
      return;
    }

    if (formData.price > 0 && breakEvenUsd !== null && formData.price < breakEvenUsd) {
      breakEvenGuard.requestSave(formData.price, breakEvenUsd, () =>
        onSubmit({ ...formData, code: normalizedCode })
      );
      return;
    }

    // Submit
    onSubmit({
      ...formData,
      code: normalizedCode,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-2">
            {mode === 'create' ? (
              <Plus size={24} className="text-blue-500" />
            ) : (
              <Edit size={24} className="text-blue-500" />
            )}
            <Typography variant="h6" className="font-semibold">
              {mode === 'create' ? 'Create New Package' : 'Edit Package'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className="space-y-4 pt-2">
          {/* Basic Information */}
          <Typography
            variant="subtitle1"
            className="font-semibold text-gray-700 dark:text-slate-300"
          >
            Basic Information
          </Typography>

          <TextField
            label="Package Code"
            value={formData.code}
            onChange={(e) => handleFieldChange('code', e.target.value.toUpperCase())}
            placeholder="BASIC, PRO, TEAM"
            fullWidth
            disabled={mode === 'edit'} // Code cannot be changed in edit mode
            helperText={
              mode === 'edit'
                ? 'Package code cannot be changed'
                : 'Use uppercase letters, numbers, underscores, or hyphens'
            }
            inputProps={{
              style: { fontFamily: 'monospace' },
            }}
          />

          <TextField
            label="Package Name"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder="e.g., Basic Plan"
            fullWidth
            required
            helperText="Display name for the package"
          />

          <PackageIntroFields
            descriptionEn={formData.descriptionEn}
            descriptionVi={formData.descriptionVi}
            highlightsEn={formData.highlightsEn}
            highlightsVi={formData.highlightsVi}
            onDescriptionChange={(next) => {
              handleFieldChange('descriptionEn', next.en);
              handleFieldChange('descriptionVi', next.vi);
            }}
            onHighlightsChange={(next) => {
              handleFieldChange('highlightsEn', next.en);
              handleFieldChange('highlightsVi', next.vi);
            }}
            disabled={isLoading}
          />

          <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormattedNumberTextField
              label="Price (USD)"
              value={formData.price}
              onValueChange={(value) => handleUsdChange(value ?? 0)}
              integer={false}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                step: 0.01,
              }}
              helperText={formatRateHint(rate)}
            />

            <FormattedNumberTextField
              label="Price (VND)"
              value={formData.priceVnd}
              onValueChange={(value) => handleVndChange(value ?? 0)}
              integer={true}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">₫</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                step: 1000,
              }}
              helperText="VND price for Vietnamese users (auto-filled from USD, editable)"
            />
          </Box>

          <PricingCalculatorPanel
            credits={formData.tokensPerMonth}
            advanceCredits={formData.advanceTokensPerMonth}
            priceUsd={formData.price}
            kind="package"
            onBreakEvenChange={setBreakEvenUsd}
            onUseSuggestedPrice={handleUsdChange}
          />

          <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormattedNumberTextField
              label="Duration (days)"
              value={formData.duration}
              onValueChange={(value) => handleFieldChange('duration', value ?? 0)}
              fullWidth
              required
              helperText="Subscription duration"
              inputProps={{
                min: 1,
              }}
            />

            <FormattedNumberTextField
              label="Max Agents"
              value={formData.maxAgents}
              onValueChange={(value) => handleFieldChange('maxAgents', value ?? 0)}
              fullWidth
              required
              helperText="Max active agents"
              inputProps={{
                min: 0,
              }}
            />

            <FormattedNumberTextField
              label="Credits per cycle"
              value={formData.tokensPerMonth}
              onValueChange={(value) => handleFieldChange('tokensPerMonth', value ?? 0)}
              fullWidth
              required
              helperText="Package credit quota"
              inputProps={{
                min: 1,
                step: 1000,
              }}
            />

            <FormattedNumberTextField
              label="Advance credits per cycle"
              value={formData.advanceTokensPerMonth}
              onValueChange={(value) => handleFieldChange('advanceTokensPerMonth', value ?? 0)}
              fullWidth
              helperText="Included advance credits for Code agent / Site builder. Resets each cycle, 0 = none"
              inputProps={{
                min: 0,
                step: 1000,
              }}
            />
          </Box>

          <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Box className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Box>
            <Box className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowDynamicTheme}
                    onChange={(e) => handleFieldChange('allowDynamicTheme', e.target.checked)}
                    color="primary"
                  />
                }
                label="Allow dynamic theme"
              />
              <Typography variant="caption" className="block text-gray-500 dark:text-slate-400">
                Users on this package can choose an animated app background
              </Typography>
            </Box>
          </Box>

          <PackageFeatureToggles
            value={formData.features}
            onChange={(next) => handleFieldChange('features', next)}
            limits={formData.featureLimits}
            onLimitsChange={(next) => handleFieldChange('featureLimits', next)}
            dailyLimits={formData.featureDailyLimits}
            onDailyLimitsChange={(next) => handleFieldChange('featureDailyLimits', next)}
            errors={featureLimitErrors}
            disabled={isLoading}
          />

          <PackageIntegrationToggles
            value={formData.integrationEntitlements}
            onChange={(next) => handleFieldChange('integrationEntitlements', next)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={mode === 'create' ? <Plus size={18} /> : <Edit size={18} />}
          disabled={isLoading}
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {isLoading
            ? mode === 'create'
              ? 'Creating...'
              : 'Updating...'
            : mode === 'create'
              ? 'Create Package'
              : 'Update Package'}
        </Button>
      </DialogActions>
      {breakEvenGuard.confirmDialog}
    </Dialog>
  );
};

// Backward compatibility export
export const CreatePackageDialog = PackageFormDialog;
